import { ProfilePicture } from '@marketing-milk/frontend'
import { Button } from '@material-ui/core'
import { FacebookPixel } from '@marketing-milk/interfaces'
import Redux from 'app/interface/redux'
import Connect from 'app/redux/connect'
import { businessManagerService } from 'app/service/business-manager'
import {
  CommonFBBusinessObjectFields,
  FBBusinessIGAccount,
} from 'app/service/business-manager/BusinessManager.types'
import axios from 'axios'
import Popover from 'components/reusable/popover'
import { debounce, groupBy } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import { toast } from 'react-toastify'

// Ad Account/Page: some accounts have hundreds of these (Facebook has no server-side name search on
// either edge — confirmed live, DRI-203 follow-up), so fetching and rendering the full list on every
// load/keystroke was the actual slow part. These two search on the backend instead (getFBPages/
// getFBAdAccounts's search+limit params, filtering the same server-cached full list — see
// filterFBObjects), capped at RESULT_LIMIT and debounced, rather than loading everything upfront.
// Dataset/Offline Dataset/Instagram Account stay on the eager-fetch-all-then-filter-client-side path
// below — they're scoped to the current Ad Account and typically far smaller lists.
const SEARCHED_TYPES: FBObjectTypeString[] = ['ad_account', 'page']
const RESULT_LIMIT = 200
const SEARCH_DEBOUNCE_MS = 300

export type FBObjectTypeString = 'page' | 'ad_account' | 'pixel' | 'offline' | 'ig'

// pixel/offline/ig are all scoped to the currently selected Ad Account (their options list is fetched
// per accountID) — when the previously-selected value isn't in that list anymore, it usually means the
// Ad Account changed underneath it, not that access was actually lost. ad_account/page have no such
// scoping, so a missing value there really does mean an access problem.
const missingSelectionMessage = (type: FBObjectTypeString): string => {
  switch (type) {
    case 'pixel':
    case 'offline':
      return 'This dataset is not connected to the currently selected Ad Account.'
    case 'ig':
      return 'This Instagram account is not connected to the currently selected Ad Account.'
    case 'ad_account':
    case 'page':
      return 'You do not have access to this Facebook asset.'
  }
}

interface FBOption {
  value: string
  label: JSX.Element
  searchables: string[]
}

const formatFBObject = ({
  id,
  name,
  picture,
  business,
}: CommonFBBusinessObjectFields): FBOption => {
  // Ad accounts/datasets have no picture of their own — fall back to the owning Business Manager's
  // picture so the selected-value box (and options) still show something, matching what the read-only
  // row below shows for the same asset.
  const imageUrl = picture?.data.url ?? business?.profile_picture_uri
  return {
    value: id,
    label: (
      <div className="col-12 d-flex">
        {imageUrl && <ProfilePicture initials={name} image={imageUrl} size={50} />}
        <div className="ml-4">
          <h5 className="font-weight-bold">{name}</h5>
          <div>{`id: ${id}`}</div>
        </div>
      </div>
    ),
    // id included so pasting/typing an asset's numeric ID finds it directly, not just matching by name.
    searchables: [name, id],
  }
}

interface FacebookObjectSelectorProps<FBObjectType extends CommonFBBusinessObjectFields>
  extends Redux {
  assignFbObject: (fbObject: FBObjectType | undefined, field: FBObjectTypeString) => void
  title: string
  type: FBObjectTypeString
  disabled: boolean
  accountID?: string
  selectedFbObjectName?: string | null
  selectedFbObjectID?: string
  // Shown for the selected value before the full options list has loaded/resolved it. Page derives its
  // own from a public Graph URL (see below); other types have no such guessable URL, so the caller can
  // supply a known picture instead (e.g. the owning Business Manager's picture for Ad Account).
  selectedFbObjectPictureUrl?: string
  tooltipText?: string
  placeholder?: string
  // Bumped by the caller whenever an in-progress pick is abandoned (Cancel, either this field's own or
  // the change-confirmation dialog's) — a picked-but-never-confirmed value would otherwise persist in
  // this component's own state across a Cancel for ad_account/page, since those two types are kept
  // mounted at all times rather than remounting fresh on next Change (see FbAssignableField's
  // `preloadable`/`showSelector`).
  resetToken?: number
  // True while a pick just made here is being assigned against Facebook (the confirm-dialog path already
  // shows its own "Verifying..." state, but a first-time pick — no prior asset, so no dialog — assigns
  // immediately with nothing else in the UI to show that a request is in flight). Feeds the select's own
  // built-in loading spinner and disables it for the duration, so the picked value doesn't just sit there
  // with no feedback while the request completes.
  assigning?: boolean
  // Ad Account/Page only: Marketing Milk caches these two lists server-side (see getFBPages/
  // getFBAdAccounts), so a newly-granted asset in Facebook may not show up here until that cache is
  // cleared. When provided, shows a "Refresh Available Assets" prompt inside the empty results message
  // (react-select's noOptionsMessage) so the fix is discoverable right where a user hits it, not only
  // via the page-level button. Omitted entirely for pixel/offline/ig, which are never cached — refreshing
  // wouldn't change anything there.
  onRefreshAssets?: () => void
  refreshingAssets?: boolean
  // Bumped by the caller after clearFacebookAccessCache resolves — re-triggers the default (empty-search)
  // fetch for ad_account/page, so the results reflect the now-cleared server cache without waiting for
  // the user to type something first.
  refreshToken?: number
  // Called once the refreshToken-triggered refetch completes (success or failure alike) — lets the
  // caller's own "Refreshing..." state cover the real fetch this field renders from, rather than just the
  // cache-clear call itself.
  onRefreshComplete?: () => void
}

const FacebookObjectSelector = <FBObjectType extends CommonFBBusinessObjectFields>(
  props: FacebookObjectSelectorProps<FBObjectType>
) => {
  const fbConnected = props.core.session.user?.fbConnected
  const [fbObject, setFbObject] = useState<{
    value: string
    label: any
    searchables: string[]
  } | null>(
    props.selectedFbObjectID
      ? formatFBObject({
          id: props.selectedFbObjectID,
          name: props.selectedFbObjectName ?? '-',
          ...(props.type === 'page'
            ? {
                picture: {
                  data: {
                    url: `https://graph.facebook.com/${props.selectedFbObjectID}/picture?type=large`,
                  },
                },
              }
            : props.selectedFbObjectPictureUrl
            ? { picture: { data: { url: props.selectedFbObjectPictureUrl } } }
            : {}),
        })
      : null
  )
  const [fbObjects, setFbObjects] = useState<FBObjectType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [searching, setSearching] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  // ad_account/page only: the user's current (uncommitted-to-state) search text — read by the
  // refreshToken effect below so a refresh re-runs against whatever's actually typed, instead of always
  // resetting back to the default (empty-search) list. A ref, not state, since updating it on every
  // keystroke shouldn't itself trigger a re-render.
  const currentSearchTerm = useRef('')

  const styles = {
    control: (provided, state) => ({
      ...provided,
      background: '#fff',
      borderColor: hasError ? '#e74c3c' : '#9e9e9e', // Red border on error
      minHeight: '75px',
      height: '75px',
      boxShadow: state.isFocused ? (hasError ? '0 0 0 1px #e74c3c' : '0 0 0 1px #2684FF') : 'none',
      '&:hover': {
        borderColor: hasError ? '#e74c3c' : '#9e9e9e',
      },
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      height: '75px',
      padding: '0 6px',
    }),
    input: (provided, state) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: state => ({
      display: 'none',
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      height: '75px',
    }),
  }

  // Resetting internal state if external state changes
  useEffect(() => {
    if (!props.selectedFbObjectID) {
      setFbObject(null)
    }
  }, [props.selectedFbObjectID])

  // Reverts an abandoned in-progress pick back to whatever is actually currently assigned, whenever the
  // caller signals a Cancel via resetToken — otherwise a picked-but-never-confirmed value would persist
  // here indefinitely for ad_account/page (see the resetToken prop comment above).
  useEffect(() => {
    if (props.resetToken === undefined) return
    setFbObject(
      props.selectedFbObjectID
        ? formatFBObject({
            id: props.selectedFbObjectID,
            name: props.selectedFbObjectName ?? '-',
            ...(props.type === 'page'
              ? {
                  picture: {
                    data: {
                      url: `https://graph.facebook.com/${props.selectedFbObjectID}/picture?type=large`,
                    },
                  },
                }
              : props.selectedFbObjectPictureUrl
              ? { picture: { data: { url: props.selectedFbObjectPictureUrl } } }
              : {}),
          })
        : null
    )
    setHasError(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.resetToken])

  // pixel/offline/ig only — ad_account/page use loadOptions below instead (see SEARCHED_TYPES comment).
  const getFBObjects = async () => {
    if (!fbConnected) return

    try {
      setLoading(true)
      const serviceRecordByType: Record<
        Exclude<FBObjectTypeString, 'ad_account' | 'page'>,
        (accountID?: string) => Promise<FBBusinessIGAccount[] | Omit<FacebookPixel, 'code'>[]>
      > = {
        pixel: (accountID?: string) =>
          !!accountID && accountID !== '0'
            ? businessManagerService.getFBDatasets(accountID)
            : Promise.resolve([]),
        offline: (accountID?: string) =>
          !!accountID && accountID !== '0'
            ? businessManagerService.getFBDatasets(accountID)
            : Promise.resolve([]),
        ig: (accountID?: string) =>
          !!accountID && accountID !== '0'
            ? businessManagerService.getFBIGAccounts(accountID)
            : Promise.resolve([]),
      }

      const response = await serviceRecordByType[props.type as 'pixel' | 'offline' | 'ig'](
        props.accountID
      )

      // We know what was selected isn't in the user's list of access
      const userLacksAccessToSelectedFBObject =
        props.selectedFbObjectID &&
        !(response as FBObjectType[]).find(({ id }) => id === props.selectedFbObjectID)

      if (userLacksAccessToSelectedFBObject) {
        setHasError(true)
      }

      setFbObjects(response as FBObjectType[])
    } catch (e) {
      if (axios.isAxiosError(e)) {
        toast.error(
          `Failed to load ${props.title.toLowerCase()}s. ${
            e instanceof Error ? e.response?.data.message : String(e)
          }.`
        )
      }

      setFbObjects([])
    } finally {
      setLoading(false)
    }
  }

  // ad_account/page only: searches the backend (see SEARCHED_TYPES comment) instead of filtering an
  // upfront full list. Writes straight into fbObjects/searching state rather than going through
  // react-select's own AsyncSelect component — that component's internal loadedOptions/passEmptyOptions
  // state machine was found to swallow correctly-returned results before render (confirmed live,
  // DRI-203 follow-up: our callback received the right data every time, but the dropdown still rendered
  // empty) — driving the plain Select's `options` prop directly from our own state sidesteps that
  // entirely. Shared by the debounced per-keystroke search below and the immediate mount/refresh fetch —
  // the latter needs to be awaitable (so a caller refreshing the cache can wait for the real refetch, not
  // just the cache-clear call), which a lodash-debounced async function can't reliably provide.
  const fetchFBObjects = async (inputValue: string) => {
    if (!fbConnected) {
      setFbObjects([])
      setSearching(false)
      return
    }
    try {
      const response =
        props.type === 'ad_account'
          ? await businessManagerService.getFBAdAccounts(inputValue, RESULT_LIMIT)
          : await businessManagerService.getFBPages(inputValue, RESULT_LIMIT)
      setFbObjects(response as FBObjectType[])
    } catch (e) {
      if (axios.isAxiosError(e)) {
        toast.error(
          `Failed to search ${props.title.toLowerCase()}s. ${
            e instanceof Error ? e.response?.data.message : String(e)
          }.`
        )
      }
      setFbObjects([])
    } finally {
      setSearching(false)
    }
  }

  // Debounced so typing doesn't fire a request per keystroke.
  const searchFBObjects = useMemo(
    () => debounce(fetchFBObjects, SEARCH_DEBOUNCE_MS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.type, fbConnected]
  )

  useEffect(() => {
    return () => searchFBObjects.cancel()
  }, [searchFBObjects])

  // ad_account/page only: fetch once up front with no search term (immediately, not debounced), so
  // opening the dropdown before typing anything already shows a first page of results (matching
  // AsyncSelect's old `defaultOptions` behavior) — and again every time refreshToken changes, re-running
  // against whatever's currently typed (currentSearchTerm) rather than always resetting back to the
  // default list, so refreshing while a search is active doesn't discard it. Awaits the real refetch
  // before reporting onRefreshComplete back to the caller.
  useEffect(() => {
    if (!SEARCHED_TYPES.includes(props.type)) return
    fetchFBObjects(currentSearchTerm.current).finally(() => props.onRefreshComplete?.())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.type, props.refreshToken])

  useEffect(() => {
    if (SEARCHED_TYPES.includes(props.type)) return
    getFBObjects()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.accountID, props.refreshToken])

  useEffect(() => {
    const selected = fbObjects.find(({ id }) => id === props.selectedFbObjectID)

    if (selected) {
      setFbObject(formatFBObject(selected))
      setHasError(false)
    }
  }, [fbObjects])

  // Group by BM id, not name — two different Business Managers can share a display name, and id is also
  // what lets the group header show a stable picture/id alongside the name.
  const getFBObjectOptionsFor = (objects: FBObjectType[]) => {
    const objectsByBusiness = groupBy(objects, o => o.business?.id ?? 'unknown')
    return Object.entries(objectsByBusiness).map(([businessID, groupObjects]) => ({
      label: groupObjects[0]?.business?.name ?? 'N/A',
      businessID: businessID !== 'unknown' ? businessID : undefined,
      businessPictureUri: groupObjects[0]?.business?.profile_picture_uri,
      options: groupObjects.map(formatFBObject),
    }))
  }

  const getFBObjectOptions = () => {
    const result = getFBObjectOptionsFor(fbObjects)
    return result
  }

  const formatGroupLabel = data => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {data.businessPictureUri && (
        <img
          src={data.businessPictureUri}
          alt={data.label}
          style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      )}
      <span>{data.label}</span>
      {data.businessID && (
        <span style={{ color: '#9e9e9e', fontWeight: 400 }}>id: {data.businessID}</span>
      )}
    </div>
  )

  const handleFBObjectSelection = selected => {
    if (selected === null) {
      // Handle clearable selects
      setFbObject(null)
      setHasError(false)
      props.assignFbObject(undefined, props.type)
    } else {
      const fbObject = fbObjects?.find(({ id }) => id === selected.value)

      if (fbObject) {
        setFbObject(selected)
        setHasError(false)
        props.assignFbObject(fbObject, props.type)
      }
    }
  }

  const noOptionsMessageComponents = props.onRefreshAssets
    ? {
        NoOptionsMessage: (optionProps: any) => (
          <div {...optionProps.innerProps} className="px-3 py-2" style={{ color: '#666' }}>
            Can&apos;t find what you&apos;re looking for?{' '}
            <Button size="small" onClick={props.onRefreshAssets} disabled={props.refreshingAssets}>
              {props.refreshingAssets ? 'Refreshing...' : 'Refresh available assets from Facebook'}
            </Button>
          </div>
        ),
      }
    : undefined

  const sharedProps = {
    className: 'col-12 pl-0',
    onChange: handleFBObjectSelection,
    value: fbObject,
    placeholder: props.placeholder ?? 'Select...',
    isDisabled: props.disabled || props.assigning,
    isClearable: true,
    formatGroupLabel,
    components: noOptionsMessageComponents,
    styles,
  }

  return (
    <div className="d-flex flex-column align-items-start px-2 py-2">
      {props.title && (
        <span className="pb-2">
          {props.title}
          {props.tooltipText && (
            <>
              &nbsp;<Popover>{props.tooltipText}</Popover>
            </>
          )}
        </span>
      )}
      {SEARCHED_TYPES.includes(props.type) ? (
        <Select
          {...sharedProps}
          options={getFBObjectOptions()}
          isLoading={searching || props.assigning}
          // The backend already searched (getFBAdAccounts/getFBPages's search param) — fbObjects here is
          // exactly the current search's results, so every option passed in is already a match. Without
          // this override, react-select's default filterOption would re-filter these against whatever's
          // currently typed using its own logic, which can disagree with the backend's (e.g. it doesn't
          // know to match against id) and hide options that did match server-side.
          filterOption={() => true}
          onInputChange={(newValue: string, actionMeta: { action: string }) => {
            // react-select fires onInputChange for non-typing actions too (menu-close, input-blur,
            // set-value on selection, etc.) — the visible input text resets to empty for all of these,
            // but fbObjects doesn't automatically follow it back to the default list; without this, the
            // dropdown would keep showing whatever the last real search happened to return (including a
            // stale empty result, if that search had no matches) every time it's reopened/reblurred with
            // nothing typed. Only actual typing (input-change) should trigger a new debounced search.
            if (actionMeta.action !== 'input-change') {
              if (newValue === '') {
                currentSearchTerm.current = ''
                searchFBObjects('')
              }
              return
            }
            currentSearchTerm.current = newValue
            setSearching(true)
            searchFBObjects(newValue)
          }}
        />
      ) : (
        <Select
          {...sharedProps}
          options={getFBObjectOptions()}
          isLoading={loading || props.assigning}
          filterOption={(option, input) => {
            const { searchables } = option.data
            for (const search of searchables) {
              if (search.toLowerCase().includes(input.toLowerCase())) {
                return true
              }
            }
            return false
          }}
        />
      )}
      {hasError && <p className="text-danger">{missingSelectionMessage(props.type)}</p>}
    </div>
  )
}

export default Connect(FacebookObjectSelector)
